import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, GraduationCap, Pencil, Trash2, Layers, BookOpen, X, Link2,
} from "lucide-react";

const empty = { title: "", description: "", thumbnail: "", category: "Wellbeing", status: "draft", portal_link: "", modules: [] };
const uid = () => Math.random().toString(36).slice(2);

export default function Courses() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });

  const save = useMutation({
    mutationFn: async (payload) =>
      editing ? (await api.put(`/courses/${editing}`, payload)).data : (await api.post("/courses", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["courses"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success(editing ? "Course updated" : "Course created");
      setOpen(false);
    },
    onError: () => toast.error("Could not save course"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/courses/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["courses"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Course deleted");
      setToDelete(null);
    },
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ ...empty, ...c, modules: c.modules || [] }); setOpen(true); };

  const addModule = () => setForm((f) => ({ ...f, modules: [...f.modules, { id: uid(), title: "", lessons: [] }] }));
  const updateModule = (mi, title) => setForm((f) => { const m = [...f.modules]; m[mi] = { ...m[mi], title }; return { ...f, modules: m }; });
  const removeModule = (mi) => setForm((f) => ({ ...f, modules: f.modules.filter((_, i) => i !== mi) }));
  const addLesson = (mi) => setForm((f) => { const m = [...f.modules]; m[mi] = { ...m[mi], lessons: [...m[mi].lessons, { id: uid(), title: "", content: "", video_url: "", resource_id: "" }] }; return { ...f, modules: m }; });
  const updateLesson = (mi, li, key, val) => setForm((f) => { const m = [...f.modules]; const ls = [...m[mi].lessons]; ls[li] = { ...ls[li], [key]: val }; m[mi] = { ...m[mi], lessons: ls }; return { ...f, modules: m }; });
  const removeLesson = (mi, li) => setForm((f) => { const m = [...f.modules]; m[mi] = { ...m[mi], lessons: m[mi].lessons.filter((_, i) => i !== li) }; return { ...f, modules: m }; });

  return (
    <div data-testid="courses-page">
      <PageHeader
        title="Courses"
        subtitle="Build supportive learning journeys with modules and lessons for your clients."
        action={
          <Button data-testid="new-course-button" onClick={openNew} className="rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600 shadow-ambient">
            <Plus className="mr-2 h-4 w-4" /> New Course
          </Button>
        }
      />

      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-white/60" />)}
        </div>
      ) : courses.length === 0 ? (
        <EmptyState onClick={openNew} />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((c) => (
            <Card key={c.id} data-testid={`course-card-${c.id}`} className="group overflow-hidden rounded-2xl border-navy-900/5 bg-white shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg">
              <div className="relative h-40 overflow-hidden bg-navy-50">
                {c.thumbnail ? (
                  <img src={c.thumbnail} alt={c.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full items-center justify-center"><GraduationCap className="h-10 w-10 text-navy-200" /></div>
                )}
                <Badge className={`absolute right-3 top-3 border-0 ${c.status === "published" ? "bg-emerald-500 text-white" : "bg-white/90 text-navy-600"}`}>
                  {c.status}
                </Badge>
              </div>
              <div className="p-5">
                <div className="text-xs font-medium uppercase tracking-wider text-gold-600">{c.category}</div>
                <h3 className="mt-1 font-serif text-lg font-bold text-navy-900">{c.title}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-navy-500">{c.description || "No description yet."}</p>
                <div className="mt-3 flex items-center gap-4 text-xs text-navy-400">
                  <span className="flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> {c.modules?.length || 0} modules</span>
                  <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> {(c.modules || []).reduce((a, m) => a + (m.lessons?.length || 0), 0)} lessons</span>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button data-testid={`edit-course-${c.id}`} variant="outline" size="sm" onClick={() => openEdit(c)} className="flex-1 rounded-full border-navy-100">
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button data-testid={`delete-course-${c.id}`} variant="outline" size="sm" onClick={() => setToDelete(c)} className="rounded-full border-navy-100 text-destructive hover:bg-destructive/10">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy-900">{editing ? "Edit Course" : "New Course"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input data-testid="course-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 focus-visible:ring-gold-500" placeholder="Foundations of Resilience" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="course-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 focus-visible:ring-gold-500" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 focus-visible:ring-gold-500" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="course-status-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Thumbnail image URL</Label>
              <Input value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} className="mt-1 focus-visible:ring-gold-500" placeholder="https://..." />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Link2 className="h-3.5 w-3.5" /> GHL portal link (optional)</Label>
              <Input value={form.portal_link} onChange={(e) => setForm({ ...form, portal_link: e.target.value })} className="mt-1 focus-visible:ring-gold-500" placeholder="Link clients open in the GHL portal" />
            </div>

            {/* Modules */}
            <div className="rounded-xl bg-ivory p-4">
              <div className="mb-3 flex items-center justify-between">
                <Label className="text-navy-800">Modules & Lessons</Label>
                <Button type="button" size="sm" variant="outline" onClick={addModule} data-testid="add-module-button" className="rounded-full border-navy-100">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Module
                </Button>
              </div>
              <div className="space-y-3">
                {form.modules.map((m, mi) => (
                  <div key={m.id} className="rounded-lg border border-navy-100 bg-white p-3">
                    <div className="flex items-center gap-2">
                      <Input value={m.title} onChange={(e) => updateModule(mi, e.target.value)} placeholder={`Module ${mi + 1} title`} className="focus-visible:ring-gold-500" />
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeModule(mi)} className="text-destructive"><X className="h-4 w-4" /></Button>
                    </div>
                    <div className="mt-2 space-y-2 pl-3">
                      {m.lessons.map((l, li) => (
                        <div key={l.id} className="flex items-center gap-2">
                          <Input value={l.title} onChange={(e) => updateLesson(mi, li, "title", e.target.value)} placeholder={`Lesson ${li + 1}`} className="h-9 text-sm focus-visible:ring-gold-500" />
                          <Button type="button" size="icon" variant="ghost" onClick={() => removeLesson(mi, li)} className="h-8 w-8 text-navy-300"><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ))}
                      <Button type="button" size="sm" variant="ghost" onClick={() => addLesson(mi)} className="text-gold-600 hover:text-gold-700">
                        <Plus className="mr-1 h-3.5 w-3.5" /> Add lesson
                      </Button>
                    </div>
                  </div>
                ))}
                {form.modules.length === 0 && <p className="text-sm text-navy-400">No modules yet. Add one to structure your course.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="save-course-button" disabled={!form.title || save.isPending} onClick={() => save.mutate(form)} className="rounded-full bg-gold-500 text-white hover:bg-gold-600">
              {save.isPending ? "Saving..." : "Save Course"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete course?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-course" onClick={() => del.mutate(toDelete.id)} className="rounded-full bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ onClick }) {
  return (
    <Card className="flex flex-col items-center justify-center rounded-2xl border-dashed border-navy-100 bg-white/60 py-20 text-center shadow-none">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gold-50"><GraduationCap className="h-8 w-8 text-gold-500" /></div>
      <h3 className="mt-5 font-serif text-xl font-bold text-navy-900">No courses yet</h3>
      <p className="mt-2 max-w-sm text-navy-500">Create your first course to give clients supportive, structured learning.</p>
      <Button onClick={onClick} className="mt-6 rounded-full bg-gold-500 text-white hover:bg-gold-600"><Plus className="mr-2 h-4 w-4" /> New Course</Button>
    </Card>
  );
}
